import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import Button from './ui/Button';
import api from '../services/api';

/**
 * UpgradeModal - Simplified Gold-themed paywall popup
 * Shows for users who have exhausted their 7 daily reflections
 * Prompts upgrade to Premium Membership - $6.99/mo
 * SELF-HEALING: Routes to Customer Portal or Checkout automatically
 */
const UpgradeModal = ({ isOpen, onClose, isSpanish = false }) => {
  const [loading, setLoading] = useState(false);

  const monthlyPrice = 6.99;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      // SELF-HEALING BILLING BRIDGE: Smart routing
      const result = await api.smartUpgrade(isSpanish ? 'es' : 'en');
      if (result.url) {
        window.location.href = result.url;
      } else if (result.detail) {
        throw new Error(result.detail);
      }
    } catch (error) {
      console.error('Error with upgrade:', error);
      const errorMessage = error.message || 'Failed to connect to payment system';
      if (errorMessage.includes('Invalid API Key') || errorMessage.includes('not configured')) {
        alert('Payment system is being set up. Please contact support at lumiveta@yahoo.com');
      } else {
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-md bg-background border border-primary/30 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Gold gradient header */}
          <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 p-6 border-b border-primary/20">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Icon name="Lock" size={32} color="#F59E0B" />
              </div>
            </div>
            <h2 className="text-2xl font-heading font-bold text-center text-gold-gradient">
              {isSpanish ? '¡Límite Diario Alcanzado!' : 'Daily Limit Reached!'}
            </h2>
            <p className="text-center text-muted-foreground mt-2">
              {isSpanish 
                ? 'Has usado tus 7 reflexiones diarias.'
                : "You've used all 7 daily reflections."}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* What you get with Premium */}
            <div className="bg-primary/5 rounded-xl p-4 mb-6 border border-primary/10">
              <p className="text-sm font-medium text-foreground mb-3">
                {isSpanish ? 'Premium Membership incluye:' : 'Premium Membership includes:'}
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Icon name="Check" size={16} color="#10B981" />
                  <span className="text-sm text-foreground">
                    {isSpanish ? '7 reflexiones diarias' : '7 daily reflections'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Check" size={16} color="#10B981" />
                  <span className="text-sm text-foreground">
                    {isSpanish ? 'Se reinician a medianoche' : 'Resets at midnight'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Check" size={16} color="#10B981" />
                  <span className="text-sm text-foreground">
                    {isSpanish ? 'Notas de Campo y audio' : 'Field Notes & audio'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Check" size={16} color="#10B981" />
                  <span className="text-sm text-foreground">
                    {isSpanish ? 'Soporte prioritario' : 'Priority support'}
                  </span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              variant="default"
              fullWidth
              onClick={handleUpgrade}
              disabled={loading}
              className="gold-glow mb-4"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  {isSpanish ? 'Cargando...' : 'Loading...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="Crown" size={18} />
                  {isSpanish ? `Suscribirse - $${monthlyPrice}/mes` : `Subscribe - $${monthlyPrice}/mo`}
                </span>
              )}
            </Button>

            {/* FTC Compliance */}
            <p className="text-xs text-muted-foreground leading-relaxed text-center">
              {isSpanish 
                ? `Se te cobrará $${monthlyPrice}/mes inmediatamente. Cancela en cualquier momento desde tu configuración.`
                : `You will be charged $${monthlyPrice}/mo immediately. Cancel anytime in your settings.`}
            </p>

            {/* Close link */}
            <button
              onClick={onClose}
              className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
            >
              {isSpanish ? 'Vuelve mañana' : 'Come back tomorrow'}
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} color="#94A3B8" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeModal;
